<template>
  <v-card flat tile :loading="loading">
    <v-toolbar dense flat>
      <TermTabs centered v-model="term" />
    </v-toolbar>
    <v-container>
      <v-card outlined class="mb-4" v-if="absences">
        <v-system-bar>Zusammenfassung</v-system-bar>
        <v-card-text class="d-flex justify-center">
          <v-chip-group column>
            <v-chip color="error" outlined
              >offen: {{ absences.pendingLessons }}</v-chip
            >

            <v-chip color="success" outlined
              >entschuldigt: {{ absences.excusedLessons }}</v-chip
            >

            <v-chip color="danger" outlined
              >unentschuldigt: {{ absences.unexcusedLessons }}</v-chip
            >

            <v-chip color="info" outlined
              >bezogene Halbtage: {{ absences.halfDays }}</v-chip
            >
          </v-chip-group>
        </v-card-text>
      </v-card>
      <v-card
        outlined
        class="mb-4"
        v-if="$_hasRole('justificationAdmin') && frequentAbsences"
      >
        <v-system-bar>Meldungen häufiger Absenzen</v-system-bar>
        <FrequentAbsencesList
          clickable
          editable
          @row-click="showFrequentAbsenceDetails"
          @toggle-email="(item) => toggleEmail(item)"
          :items="frequentAbsences"
        />
      </v-card>
      <v-card outlined class="mb-4" v-if="absences">
        <v-system-bar>Begründungen</v-system-bar>

        <v-data-table
          dense
          :loading="loading"
          :headers="justificationHeaders"
          :items="justifications"
          disable-pagination
          hide-default-footer
          single-expand
          item-key="id"
          @click:row="(item) => showDetails(item.id)"
        >
          <template v-slot:item.startDate="{ item }">
            <DateValue :value="item.startDate" />
          </template>
          <template v-slot:item.endDate="{ item }">
            <DateValue :value="item.endDate" />
          </template>
        </v-data-table>
      </v-card>
      <v-card outlined class="mb-4" v-if="absences">
        <v-system-bar>Unentschuldigte Absenzen</v-system-bar>
        <v-data-table
          dense
          :loading="loading"
          :headers="unexcusedHeaders"
          :items="unexcused"
          disable-pagination
          hide-default-footer
          single-expand
          item-key="id"
        >
          <template v-slot:item.date="{ item }">
            <DateValue :value="item.date" />
          </template>
        </v-data-table>
      </v-card>
      <v-card outlined class="mb-4" v-if="absences">
        <v-system-bar>Ignorierte Absenzen</v-system-bar>
        <v-data-table
          dense
          :loading="loading"
          :headers="ignoredHeaders"
          :items="ignored"
          disable-pagination
          hide-default-footer
          single-expand
          item-key="id"
        >
          <template v-slot:item.date="{ item }">
            <DateValue :value="item.date" />
          </template>
        </v-data-table>
      </v-card>
      <v-card outlined class="mb-4" v-if="absences">
        <v-system-bar>Heat Map</v-system-bar>
        <JustificationHeatmap :id="id" :term="term"></JustificationHeatmap>
      </v-card>

      <v-card outlined class="mb-4" :loading="loadingExamAbsences">
        <v-system-bar>verpasste Prüfungen</v-system-bar>
        <v-alert type="info" text
          >Es wird nur überprüft ob der/die Schüler/in in einer Lektion abwesend
          war, wo im Kurs eine Probe eingetragen war! (Doppellektionen
          erscheinen zweimal, es können auch z.B. Vorträge erscheinen, welche
          diese Person nicht direkt betreffen)</v-alert
        >
        <v-list>
          <v-list-item
            v-for="(examAbsence, index) of examAbsences"
            :key="index"
          >
            <v-list-item-content>
              <v-list-item-title>
                {{ formatDate(examAbsence.absence.date, true) }},
                {{ examAbsence.absence.startTime }} –
                {{ examAbsence.absence.endTime }}
              </v-list-item-title>
              <v-list-item-subtitle>
                Absenz:
                <strong>
                  <span v-if="examAbsence.absence.justificationCategory">
                    {{
                      examAbsence.absence.justificationCategory.description
                    }}</span
                  >
                  ({{ examAbsence.absence.status.description }})</strong
                >, gemeldet von {{ examAbsence.absence.teacher.code }}
              </v-list-item-subtitle>
              <v-list-item-subtitle>
                Kurs:
                <strong>{{ examAbsence.exam.course.title }}</strong>
                ({{
                  examAbsence.exam.course.teachers
                    .map((teacher) => teacher.code)
                    .join(", ")
                }})
              </v-list-item-subtitle>
              <v-list-item-subtitle>
                Prüfung:
                <strong>{{ examAbsence.exam.title }}</strong>
                {{ examAbsence.exam.description }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>
    </v-container>
    <router-view
      @saved="fetchFrequentAbsences"
      :termId="term ? term.id : 0"
      :personId="personId"
    ></router-view>
  </v-card>
</template>

<script>
import { defineComponent } from "vue";
import { saveChildRoute } from "@/router/helper";
import DateValue from "common/components/DateValue.vue";
import FrequentAbsencesList from "common/components/Absences/FrequentAbsencesList.vue";
import JustificationHeatmap from "common/components/Absences/JustificationHeatmap";
import RowItem from "@/components/RowItem";
import TermTabs from "@/components/TermTabs";

import { formatDate } from "common/utils/date";

export default defineComponent({
  name: "PersonAbsences",
  components: {
    DateValue,
    FrequentAbsencesList,
    JustificationHeatmap,
    RowItem,
    TermTabs,
  },
  props: {
    id: { type: String },
  },
  computed: {
    personId() {
      return Number.parseInt(this.id);
    },
    principal() {
      return this.$_hasRole("principal");
    },
  },
  data() {
    return {
      absences: {},
      examAbsences: [],
      frequentAbsences: [],
      justifications: [],
      justificationHeaders: [
        { text: "von", value: "startDate" },
        { text: "bis", value: "endDate" },
        { text: "Kategorie", value: "category.description" },
        { text: "Entschuldigung", value: "description" },
        { text: "Status", value: "status.description" },
        { text: "L", value: "lessonCount", align: "right" },
      ],
      loading: false,
      loadingExamAbsences: false,
      loadingFrequentAbsences: false,
      term: null,
      unexcused: [],
      unexcusedHeaders: [
        { text: "Datum", value: "date" },
        { text: "von", value: "startTime" },
        { text: "bis", value: "endTime" },
        { text: "Beschreibung", value: "description" },
      ],
      ignored: [],
      ignoredHeaders: [
        { text: "Datum", value: "date" },
        { text: "von", value: "startTime" },
        { text: "bis", value: "endTime" },
        { text: "Beschreibung", value: "description" },
      ],
    };
  },
  methods: {
    formatDate,
    showFrequentAbsenceDetails(item) {
      if (!this.$_hasRole("justificationAdmin")) {
        return;
      }
      this.$router.push({
        name: "PersonAbsencesFrequent",
        params: {
          frequentAbsencesId: item.id,
          personId: this.personId,
          termId: this.term.id,
        },
      });
    },
    async toggleEmail(item) {
      if (!this.$_hasRole("justificationAdmin")) {
        return;
      }
      console.log("toggleEmail", item);
      await this.apiPatch({
        resource: "absence/frequentAbsences",
        id: item.id,
        key: "emailSent",
        value: !item.emailSent,
      });
      this.fetchFrequentAbsences();
    },
    async getData() {
      if (this.personId && this.term) {
        this.loading = true;
        this.absences = await this.apiGet({
          resource: "absence/overview",
          id: this.personId,
          query: `term=${this.term.id}`,
        });
        this.justifications = await this.apiList({
          resource: "absence/justification",
          query: `student=${this.personId}&startDate=${this.term.startDate}&endDate=${this.term.endDate}`,
        });
        this.unexcused = await this.apiList({
          resource: "absence/absence",
          query: `student=${this.id}&unexcused&startDate=${this.term.startDate}&endDate=${this.term.endDate}`,
        });
        this.ignored = await this.apiList({
          resource: "absence/absence",
          query: `student=${this.id}&ignored&startDate=${this.term.startDate}&endDate=${this.term.endDate}`,
        });
        this.loading = false;
        this.fetchExamAbsences();
        this.fetchFrequentAbsences();
      }
    },

    async fetchExamAbsences() {
      this.loadingExamAbsences = true;

      this.examAbsences = (
        await this.apiGet({
          resource: "absence/exam",
          id: this.id,
          query: `term=${this.term.id}`,
        })
      ).items;

      this.loadingExamAbsences = false;
    },
    async fetchFrequentAbsences() {
      if (!this.$_hasRole("justificationAdmin")) {
        return;
      }
      this.loadingFrequentAbsences = true;

      this.frequentAbsences = await this.apiList({
        resource: "absence/frequentAbsences",
        query: `student=${this.id}&startDate=${this.term.startDate}&endDate=${this.term.endDate}`,
      });

      this.loadingFrequentAbsences = false;
    },
    showDetails(id) {
      if (!this.$_hasRole("justificationAdmin")) {
        return;
      }
      this.$router.push({
        name: "PersonAbsencesJustification",
        params: { justificationId: id },
      });
    },
  },
  watch: {
    async id() {
      await this.getData();
    },
    async term() {
      await this.getData();
    },
  },
  async created() {
    await this.getData();
  },
  beforeRouteUpdate(to, from, next) {
    saveChildRoute(to, next);
  },
});
</script>
